import { CfdiUses, CfdiUseByTaxRegime } from '@subscriptions/domain/types';
import type { CfdiUseType } from '@subscriptions/domain/types';

/**
 * Option in the CFDI usage catalog consumed by presentation components.
 * `value` is the SAT code stored by the domain; `label` is the i18n key
 * that resolves to the human-readable description.
 */
export type CfdiUseOption = {
  label: string;
  value: CfdiUseType;
};

/**
 * Presentation-level catalog for CFDI usage options.
 *
 * Mirrors the shape of the legacy `optionsUseInvoice` so forms can render a
 * select or resolve a label from a stored SAT code without touching domain.
 */
export const cfdiUseOptions: readonly CfdiUseOption[] = [
  { label: `invoiceData:cfdiValues.${CfdiUses.UNDEFINED}`, value: CfdiUses.UNDEFINED },
  { label: `invoiceData:cfdiValues.${CfdiUses.GENERAL_EXPENSES}`, value: CfdiUses.GENERAL_EXPENSES },
  { label: `invoiceData:cfdiValues.${CfdiUses.MERCHANDISE_ACQUISITION}`, value: CfdiUses.MERCHANDISE_ACQUISITION },
];

/**
 * Fallback CFDI usage applied when the selected regime is empty or has no
 * explicit mapping in {@link CfdiUseByTaxRegime}.
 */
const DEFAULT_CFDI_USE: CfdiUseType = CfdiUses.GENERAL_EXPENSES;

/**
 * Resolves the CFDI usage (c_UsoCFDI) that must be pre-selected for a given SAT
 * fiscal regime code (c_RegimenFiscal), based on the {@link CfdiUseByTaxRegime}
 * catalog mapping. Unknown or empty codes fall back to "Gastos en general" (G03).
 *
 * @param taxRegimeSatCode - SAT fiscal regime code selected in the form
 * @returns CFDI usage SAT code to pre-select
 */
export const getCfdiUseForTaxRegime = (taxRegimeSatCode: string): CfdiUseType =>
  CfdiUseByTaxRegime[taxRegimeSatCode as keyof typeof CfdiUseByTaxRegime] ?? DEFAULT_CFDI_USE;
